/**
 * Typed body of `POST api/AssetInfo/RecompartmentElement/` (ThreeD).
 * - 200: the element was moved; `GltfFailedRegionIds` lists the regions whose
 *   3D model could not be regenerated (regionId -> error text).
 * - 409: rejected before anything was written (same body, `Success = false`).
 * - 400: unexpected exception during the move (same body), or no body at all
 *   for a null request.
 */
export interface RecompartmentElementResult {
  Success: boolean
  Error?: string
  FailedRegionId?: string
  RegeneratedRegionIds?: string[]
  GltfFailedRegionIds?: Record<string, string>
}

/** A region whose GLTF could not be regenerated after a successful move. */
export interface GltfRegionFailure {
  regionId: string
  regionName: string
  error: string
}

export interface RecompartmentOutcome {
  /** The element was moved on the backend. */
  ok: boolean
  /** Notification title for a failed move (`ok === false`). */
  failureMessage?: string
  /** Regions whose 3D model could not be regenerated (only when `ok`). */
  gltfFailures: GltfRegionFailure[]
  /** One warning line per failed GLTF (only when `ok`). */
  warningMessages: string[]
}

/**
 * Pure interpretation of the ThreeD recompartment response.
 *
 * @param result     the response body on HTTP 200 (null/undefined when there is none)
 * @param httpError  the HttpErrorResponse-like error when the call failed (409/400/network)
 * @param regionName resolves a region id to a display name (falls back to the id)
 */
export function describeRecompartmentResult(
  result: RecompartmentElementResult | null | undefined,
  httpError: { status?: number; error?: any; message?: string } | null | undefined,
  regionName: (regionId: string) => string | null | undefined,
): RecompartmentOutcome {
  const nameOf = (regionId: string) => regionName(regionId) || regionId
  if (httpError) {
    const body = httpError.error && typeof httpError.error === 'object' ? httpError.error as RecompartmentElementResult : null
    const reason = body?.Error ?? (typeof httpError.error === 'string' && httpError.error.length > 0 ? httpError.error : null) ?? 'failed'
    return { ok: false, failureMessage: 'Changing Regions: ' + reason, gltfFailures: [], warningMessages: [] }
  }
  // A body with Success === false on a 2xx is still a rejected move. An empty
  // body (older ThreeD without the typed result) is treated as success.
  if (result && result.Success === false) {
    return { ok: false, failureMessage: 'Changing Regions: ' + (result.Error ?? 'failed'), gltfFailures: [], warningMessages: [] }
  }
  const gltfFailures: GltfRegionFailure[] = Object.entries(result?.GltfFailedRegionIds ?? {})
    .map(([regionId, error]) => ({ regionId, regionName: nameOf(regionId), error: error ?? '' }))
  const warningMessages = gltfFailures.map(f => `3D model of region ${f.regionName} could not be regenerated${f.error ? ': ' + f.error : ''}`)
  return { ok: true, gltfFailures, warningMessages }
}
